import { memo, useState, useMemo } from "react";

import cc from "classcat";
import { InstitutionLogo } from "cerulean";

import TopMenu from "../../../../components/topMenu/TopMenuComponent";
import { useSidebarContainer, useIsSidebarEnabled } from "../sidebar.hooks";
import { useSidebarMenuContainer } from "../sidebar.menu.item.hooks";
import { useSidebarRouteHighlighting } from "../sidebar.highlighting";
import SidebarProfileSwitcher from "../SidebarProfileSwitcher/SidebarProfileSwitcher";
import SidebarUpdateCard from "../SidebarUpdateCard/SidebarUpdateCard";
import { AccountContextProvider } from "../../../../components/contexts/AccountContext";
import { SidebarGuide } from "../SidebarGuide";
import ExistingUserSidebarGuides from "../SidebarGuide/ExistingUserSidebarGuides";
import SidebarMarketingCardContainer from "../SidebarMarketingCard/SidebarMarketingCardContainer";

import styles from "./Sidebar.module.scss";
import ExpandCollapseButton from "./ExpandCollapseButton";
import MenuItem from "./MenuItem";

import type { MainLayoutProps } from "../../../../components/Main/types";

const Sidebar = (props: MainLayoutProps) => {
  const { isSidebarEnabled, isOptOutEnabled } = useIsSidebarEnabled();
  const { isCollapsed, isMenuGroupExpanded, toggleCollapseClass } =
    useSidebarContainer();
  const sidebarMenuItems = useSidebarMenuContainer({
    legacyNafUrls: props.legacyNafUrls,
  });
  const { getMenuHighlightState } =
    useSidebarRouteHighlighting(sidebarMenuItems);

  const [showExpandIcon, setShowExpandIcon] = useState(false);

  const favicon = useMemo(() => {
    const nodeList = document.getElementsByTagName("link");
    for (let i = 0; i < nodeList.length; i += 1) {
      if (
        nodeList[i].getAttribute("rel") === "icon" ||
        nodeList[i].getAttribute("rel") === "shortcut icon"
      ) {
        return nodeList[i].getAttribute("href");
      }
    }
    return null;
  }, []);

  const sidebarClasses = cc([
    styles["sidebar-body"],
    isCollapsed && styles["sidebar-body--collapsed"],
  ]);
  const menuItemsContainerClasses = cc([styles["menu-items-container"]]);

  if (!isSidebarEnabled) {
    return <TopMenu {...props} />;
  }

  return (
    <AccountContextProvider accounts={props.accounts}>
      <nav
        className={sidebarClasses}
        onMouseEnter={() => isCollapsed && setShowExpandIcon(true)}
        onMouseLeave={() => isCollapsed && setShowExpandIcon(false)}
      >
        <div className={styles["sidebar-logo-container"]}>
          {isCollapsed && !showExpandIcon && favicon ? (
            <div className={styles["sidebar-favicon-container"]}>
              <img
                src={favicon}
                alt={props.institution?.name || "Logo"}
                className={styles["sidebar-favicon"]}
              />
            </div>
          ) : (
            <>
              <InstitutionLogo
                institution={props.institution}
                className={cc([
                  "nds-navbar-logo",
                  styles["nds-navbar-logo"],
                  isCollapsed && styles["nds-navbar-logo--collapsed"],
                ])}
              />
              {(!isCollapsed || showExpandIcon) && <ExpandCollapseButton />}
            </>
          )}
        </div>

        <SidebarProfileSwitcher isCollapsed={isCollapsed} {...props} />
        <SidebarGuide />

        <div className={menuItemsContainerClasses} role="menu">
          {sidebarMenuItems.map((menuItem, index) => {
            const { isActive, isOpen, onUserToggle } =
              getMenuHighlightState(menuItem);
            const { key: _key, ...menuItemProps } = menuItem;
            const itemKey = _key || `menu_item_${index}`;
            return (
              <div
                className={cc([
                  styles["menu-item-background"],
                  isMenuGroupExpanded(itemKey) &&
                    styles["menu-item-background--expanded"],
                  isCollapsed && styles["menu-item-background--collapsed"],
                ])}
                key={itemKey}
                onClick={() => {
                  if (
                    isCollapsed &&
                    !menuItem.route &&
                    menuItem?.menuOptions?.length
                  ) {
                    toggleCollapseClass();
                    onUserToggle();
                  }
                }}
              >
                <MenuItem
                  itemKey={itemKey}
                  isOpen={isOpen}
                  isActive={isActive}
                  onUserToggle={onUserToggle}
                  {...menuItemProps}
                />
              </div>
            );
          })}
        </div>

        <div className={styles["sidebar-additional-features-container"]}>
          <SidebarMarketingCardContainer isCollapsed={isCollapsed} />
          <ExistingUserSidebarGuides isCollapsed={isCollapsed} />
          {isOptOutEnabled && <SidebarUpdateCard isCollapsed={isCollapsed} />}
        </div>
      </nav>
    </AccountContextProvider>
  );
};

export default memo(Sidebar);
